import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Check, X, MessageSquare, Send, ChevronLeft, 
  ChevronRight, User, Building, AlertCircle
} from 'lucide-react';
import { useAnalytics } from '../hooks/useAnalytics';

const ClientReviewPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const [board, setBoard] = useState(null);
  const [designer, setDesigner] = useState(null);
  const [items, setItems] = useState([]);
  const [comments, setComments] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [newComment, setNewComment] = useState('');
  const [finalComment, setFinalComment] = useState('');
  const [showFinalModal, setShowFinalModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { trackClientFeedback } = useAnalytics();

  const fetchReviewData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/client-approval/review/${token}`);
      setSession(res.data.session);
      setBoard(res.data.board);
      setDesigner(res.data.designer);
      setItems(res.data.items || []);
      setComments(res.data.comments || []);
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        setError('Link di revisione non valido o scaduto');
      } else if (status === 400) {
        setError(err.response?.data?.detail || 'Link scaduto');
      } else {
        setError('Errore nel caricamento della revisione');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchReviewData();
  }, [fetchReviewData]);

  const handleFeedback = async (itemId, status) => {
    try {
      await axios.post(`${API}/client-approval/review/${token}/feedback/${itemId}`, {
        status,
        comment: null
      });

      // Track feedback event
      const item = items.find(i => i.item_id === itemId);
      if (item?.material_id) {
        trackClientFeedback(item.material_id, status);
      }

      // Update local state
      setItems(items.map(item => {
        if (item.item_id === itemId) {
          return {
            ...item,
            feedback: { ...item.feedback, status }
          };
        }
        return item;
      }));

      toast.success(status === 'approved' ? 'Approvato!' : status === 'rejected' ? 'Rifiutato' : 'Segnato come indeciso');
    } catch (err) {
      toast.error('Errore nel salvataggio del feedback');
    }
  };

  const handleItemComment = async (itemId, comment) => {
    if (!comment.trim()) return;
    
    try {
      await axios.post(`${API}/client-approval/review/${token}/feedback/${itemId}`, {
        status: items.find(i => i.item_id === itemId)?.feedback?.status || 'undecided',
        comment
      });

      setItems(items.map(item => {
        if (item.item_id === itemId) {
          return {
            ...item,
            feedback: { ...item.feedback, comment }
          };
        }
        return item;
      }));

      toast.success('Commento salvato');
    } catch (err) {
      toast.error('Errore nel salvataggio del commento');
    }
  };

  const handleAddGeneralComment = async () => {
    if (!newComment.trim()) return;

    try {
      const res = await axios.post(`${API}/client-approval/review/${token}/comment`, {
        comment: newComment
      });

      setComments([...comments, res.data.comment]);
      setNewComment('');
      toast.success('Commento aggiunto');
    } catch (err) {
      toast.error('Errore nell\'aggiunta del commento');
    }
  };

  const handleCompleteReview = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/client-approval/review/${token}/complete`, {
        overall_comment: finalComment
      });

      toast.success('Revisione completata! Il designer è stato notificato.');
      setShowFinalModal(false);
      
      // Refresh to show updated status
      fetchReviewData();
    } catch (err) {
      toast.error('Errore nel completamento della revisione');
    } finally {
      setSubmitting(false);
    }
  };

  const currentItem = items[currentIndex];
  const approvedCount = items.filter(i => i.feedback?.status === 'approved').length;
  const rejectedCount = items.filter(i => i.feedback?.status === 'rejected').length;
  const pendingCount = items.length - approvedCount - rejectedCount;

  const getStatusStyle = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-500 text-white';
      case 'rejected': return 'bg-red-500 text-white';
      default: return 'bg-gray-200 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Caricamento revisione...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-8 max-w-md w-full text-center shadow-lg">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Errore</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold" data-testid="project-name">{board?.project_name || board?.name}</h1>
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <User className="w-4 h-4" />
                {designer?.name}
                {designer?.studio && (
                  <>
                    <Building className="w-4 h-4 ml-2" />
                    {designer.studio}
                  </>
                )}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm">
                <span className="text-green-600 font-medium">{approvedCount}</span>
                <span className="text-muted-foreground mx-1">/</span>
                <span className="text-red-600 font-medium">{rejectedCount}</span>
                <span className="text-muted-foreground mx-1">/</span>
                <span className="text-gray-600 font-medium">{pendingCount}</span>
              </div>
              <Button 
                onClick={() => setShowFinalModal(true)}
                disabled={pendingCount === items.length}
                data-testid="complete-review-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Completa Revisione
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Material Navigation - Mobile Scroll / Desktop Grid */}
          <div className="lg:col-span-1 order-2 lg:order-1">
            <div className="bg-white rounded-xl p-4 shadow-sm sticky top-24">
              <h2 className="font-semibold mb-4">Materiali ({items.length})</h2>
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {items.map((item, idx) => (
                  <button
                    key={item.item_id}
                    onClick={() => setCurrentIndex(idx)}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors ${
                      idx === currentIndex 
                        ? 'bg-primary/10 border border-primary' 
                        : 'hover:bg-gray-50 border border-transparent'
                    }`}
                    data-testid={`material-nav-${item.item_id}`}
                  >
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                      {item.material?.images?.[0] && (
                        <img 
                          src={item.material.images[0]} 
                          alt={item.material?.name}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <p className="font-medium text-sm truncate">{item.material?.name}</p>
                      <p className="text-xs text-muted-foreground">{item.material?.stone_type}</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${getStatusStyle(item.feedback?.status)}`}>
                      {item.feedback?.status === 'approved' && <Check className="w-4 h-4" />}
                      {item.feedback?.status === 'rejected' && <X className="w-4 h-4" />}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Current Material Detail */}
          <div className="lg:col-span-2 order-1 lg:order-2">
            {currentItem && (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                {/* Image */}
                <div className="aspect-[4/3] bg-gray-100">
                  {currentItem.material?.images?.[0] ? (
                    <img 
                      src={currentItem.material.images[0]} 
                      alt={currentItem.material?.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      Nessuna immagine
                    </div>
                  )}
                </div>

                {/* Navigation Arrows */}
                <div className="flex justify-between px-4 -mt-6 relative z-10">
                  <button
                    onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                    disabled={currentIndex === 0}
                    className="w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center disabled:opacity-50"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={() => setCurrentIndex(Math.min(items.length - 1, currentIndex + 1))}
                    disabled={currentIndex === items.length - 1}
                    className="w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center disabled:opacity-50"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </div>

                {/* Material Info */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-bold" data-testid="current-material-name">
                        {currentItem.material?.name}
                      </h2>
                      <p className="text-muted-foreground">{currentItem.material?.stone_type}</p>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {currentIndex + 1} / {items.length}
                    </span>
                  </div>

                  {currentItem.material?.description && (
                    <p className="text-muted-foreground mb-6">{currentItem.material.description}</p>
                  )}

                  {/* Feedback Buttons */}
                  <div className="flex gap-3 mb-6">
                    <button
                      onClick={() => handleFeedback(currentItem.item_id, 'approved')}
                      className={`flex-1 py-4 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors ${
                        currentItem.feedback?.status === 'approved'
                          ? 'bg-green-500 text-white'
                          : 'bg-green-50 text-green-700 hover:bg-green-100'
                      }`}
                      data-testid="approve-btn"
                    >
                      <Check className="w-5 h-5" />
                      Approva
                    </button>
                    <button
                      onClick={() => handleFeedback(currentItem.item_id, 'rejected')}
                      className={`flex-1 py-4 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors ${
                        currentItem.feedback?.status === 'rejected'
                          ? 'bg-red-500 text-white'
                          : 'bg-red-50 text-red-700 hover:bg-red-100'
                      }`}
                      data-testid="reject-btn"
                    >
                      <X className="w-5 h-5" />
                      Rifiuta
                    </button>
                  </div>

                  {/* Comment Input */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      <MessageSquare className="w-4 h-4 inline mr-1" />
                      Commento su questo materiale
                    </label>
                    <textarea
                      value={currentItem.feedback?.comment || ''}
                      onChange={(e) => {
                        const newItems = [...items];
                        newItems[currentIndex] = {
                          ...newItems[currentIndex],
                          feedback: { ...newItems[currentIndex].feedback, comment: e.target.value }
                        };
                        setItems(newItems);
                      }}
                      onBlur={(e) => handleItemComment(currentItem.item_id, e.target.value)}
                      placeholder="Aggiungi un commento opzionale..."
                      className="w-full border rounded-lg p-3 h-24 resize-none"
                      data-testid="material-comment"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* General Comments */}
            <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
              <h3 className="font-semibold mb-4">Commenti Generali sul Progetto</h3>
              
              {comments.length > 0 && (
                <div className="space-y-3 mb-4">
                  {comments.map((comment) => (
                    <div key={comment.comment_id} className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm">{comment.comment}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(comment.created_at).toLocaleString('it-IT')}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Scrivi un commento generale..."
                  className="flex-1 border rounded-lg px-4 py-2"
                  data-testid="general-comment-input"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddGeneralComment()}
                />
                <Button onClick={handleAddGeneralComment} data-testid="add-comment-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Complete Review Modal */}
      {showFinalModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Completa Revisione</h2>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span>Approvati:</span>
                <span className="font-medium text-green-600">{approvedCount}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span>Rifiutati:</span>
                <span className="font-medium text-red-600">{rejectedCount}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Non valutati:</span>
                <span className="font-medium text-gray-600">{pendingCount}</span>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Commento finale (opzionale)
              </label>
              <textarea
                value={finalComment}
                onChange={(e) => setFinalComment(e.target.value)}
                placeholder="Lascia un messaggio al designer..."
                className="w-full border rounded-lg p-3 h-24 resize-none"
                data-testid="final-comment"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowFinalModal(false)}>
                Annulla
              </Button>
              <Button onClick={handleCompleteReview} disabled={submitting} data-testid="confirm-complete-btn">
                {submitting ? 'Invio...' : 'Invia Feedback'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientReviewPage;
